/**
 * Typy dla profilu zalogowanego użytkownika c_user_accounts.
 *
 * **Plik shared client/server** — bez `import "server-only"` bo client
 * components (ProfileTabPersonal, ProfileTabs) używają tych typów + helperów
 * `didShowField`/`isFieldRequired` (pure functions, zero server deps).
 * Route handlery server-side i tak nie potrzebują "server-only" guard'a tutaj —
 * fetch/lookup user data idzie przez `apiClient` (które ma `server-only`).
 *
 * **Backend source:** `IAPI_user` (`GET /IRESTAPI/v1/IIAPI_CONTROLLER/user`,
 * `requiresUser: true`) zwraca rezultat `IUSER::getData(user()->id, $type)`
 * + `fields_config` z `IUSERS_MANAGER_CONTROLLER::getFieldsConfig($groups)`.
 *
 * Pola whitelistowane do update przez `IAPI_userUpdate` — zob.
 * `IFILTER::filterVars` w iapi.php:4517-4521.
 */

/**
 * Pojedyncze pole formularza profilu — wartość konfiguracji per-grupa:
 *   - `'0'` lub brak -> field HIDDEN (nie pokazywać w form)
 *   - `'1'` -> optional (pokazać, NIE oznaczać required)
 *   - `'2'` -> required (pokazać + walidacja required)
 *
 * **Type może być string LUB number** — PHP serializuje fields_config z różnych
 * źródeł (form POST, DB, hardcoded defaults), wartości mogą lądować w JSON
 * jako string `"1"` lub number `1`. Helpery `didShowField`/`isFieldRequired`
 * porównują tolerantnie (`== "1" || === 1`).
 *
 * Backend helpery `didShowField` / `isFieldRequired` w
 * `IUSERS_MANAGER_CONTROLLER` (`ADMIN/pages/users_manager/users_manager.php`).
 */
export type FieldVisibility = "0" | "1" | "2" | 0 | 1 | 2 | null | undefined;

/**
 * fields_config — struktura per-section z konfiguracji grup użytkownika.
 * Backend agreguje wartości po user's groups (przez `>` comparison — najsilniejsza
 * wygrywa: required > optional > hidden).
 *
 * Plus computed `show_*_tab` flags (true gdy któreś pole w sekcji ma value 1 lub 2).
 */
export type UserFieldsConfig = {
  /* Computed flags — czy w ogóle pokazać tab (choć jedno pole widoczne). */
  show_personal_tab?: boolean;
  show_company_tab?: boolean;
  show_billing_tab?: boolean;
  show_prices_tab?: boolean;
  show_commercial_information_tab?: boolean;
  show_shipping_tab?: boolean;
  show_contacts_tab?: boolean;
  show_employee_tab?: boolean;

  /* Per-section pola — klucz = nazwa pola backendowego, value = '0'|'1'|'2'. */
  personal?: Record<string, FieldVisibility>;
  company?: Record<string, FieldVisibility>;
  billing?: Record<string, FieldVisibility>;
  prices?: Record<string, FieldVisibility>;
  employee?: Record<string, FieldVisibility>;
  commercial_information?: Record<string, FieldVisibility>;
  shipping?: Record<string, FieldVisibility>;
  contacts?: Record<string, FieldVisibility>;
};

/**
 * Profile data zwrócone przez `IAPI_user`. Zawiera wszystkie kolumny `imp_user`
 * + `fields_config` + computed fields (`fullname`, `masked_email`, etc.).
 *
 * Pola TUTAJ są **read-side** — w update wysyłamy podzbiór (whitelist
 * `IFILTER::filterVars`).
 */
export type UserProfile = {
  id: number;
  login: string;
  email: string | null;
  active: string | number;
  enabled: string | number;
  id_user_group: number;
  groups: number[];

  /* Personal — z `IFILTER::filterVars` personal sekcji. */
  name?: string | null;
  surname?: string | null;
  sex?: string | null;
  birth_date?: string | null;
  about_me_pl?: string | null;
  about_me_en?: string | null;
  about_me_de?: string | null;
  country?: string | null;
  city?: string | null;
  post_code?: string | null;
  street?: string | null;
  nr?: string | null;
  state?: string | null;
  province?: string | null;
  telephone?: string | null;
  www?: string | null;
  pesel?: string | null;
  nip?: string | null;
  nr_id_card?: string | null;
  bank_account_number?: string | null;
  facebook_account_url?: string | null;
  twitter_account_url?: string | null;
  pinterest_account_url?: string | null;
  youtube_account_url?: string | null;
  linkedin_account_url?: string | null;

  /* Company — z `IFILTER::filterVars` company sekcji. */
  company_name?: string | null;
  company_city?: string | null;
  company_post_code?: string | null;
  company_street?: string | null;
  company_nr?: string | null;
  company_state?: string | null;
  company_province?: string | null;
  company_country?: string | null;
  company_telephone?: string | null;
  company_fax?: string | null;
  company_www?: string | null;
  company_krs?: string | null;
  company_nip?: string | null;
  regon?: string | null;
  company_contact_person_name?: string | null;
  company_contact_person_surname?: string | null;
  company_trade?: string | null;
  company_bank_account_number?: string | null;

  /* Billing — z `IFILTER::filterVars` billing sekcji. */
  billing_name?: string | null;
  billing_country?: string | null;
  billing_city?: string | null;
  billing_post_code?: string | null;
  billing_street?: string | null;
  billing_nr?: string | null;
  billing_nip?: string | null;

  /* Consents + meta. */
  regulamin?: string | number | null;
  marketing?: string | number | null;
  personal_data?: string | number | null;

  /* Computed (z IUSER::getData) — read-only. */
  fullname?: string;
  masked_email?: string;
  avatar?: string | null;
  /** Computed URLs (path relative) — oryginał, miniatura sq_ (120×120) i s_
      (120px proportional). Frontend dokleja base URL przez NEXT_PUBLIC_IMPERIAL_DOMAIN.
      Null gdy user nie ma avatara. */
  avatar_url?: string | null;
  avatar_sq_url?: string | null;
  avatar_s_url?: string | null;
  tmp_data?: Record<string, unknown>;

  /* fields_config z IUSERS_MANAGER_CONTROLLER::getFieldsConfig. */
  fields_config: UserFieldsConfig;
};

/**
 * Whitelist pól które wolno wysłać w `IAPI_userUpdate` (zob. iapi.php:4517-4521).
 * UI nie powinien pozwalać użytkownikowi edytować pól spoza tej listy — backend
 * je i tak odfiltruje przez `IFILTER::filterVars`.
 */
export type UserProfileUpdate = Partial<{
  /* Personal */
  name: string;
  surname: string;
  sex: string;
  birth_date: string;
  about_me_pl: string;
  about_me_en: string;
  about_me_de: string;
  city: string;
  post_code: string;
  street: string;
  nr: string;
  state: string;
  province: string;
  country: string;
  telephone: string;
  www: string;
  pesel: string;
  nip: string;
  nr_id_card: string;
  bank_account_number: string;
  facebook_account_url: string;
  twitter_account_url: string;
  pinterest_account_url: string;
  youtube_account_url: string;
  linkedin_account_url: string;

  /* Company */
  company_name: string;
  company_city: string;
  company_post_code: string;
  company_street: string;
  company_nr: string;
  company_state: string;
  company_province: string;
  company_country: string;
  company_telephone: string;
  company_fax: string;
  company_www: string;
  company_krs: string;
  company_nip: string;
  regon: string;
  company_contact_person_name: string;
  company_contact_person_surname: string;
  company_trade: string;
  company_bank_account_number: string;

  /* Billing */
  billing_name: string;
  billing_country: string;
  billing_city: string;
  billing_post_code: string;
  billing_street: string;
  billing_nr: string;
  billing_nip: string;

  /* Consents */
  marketing: 0 | 1;
  regulamin: 0 | 1;
  personal_data: 0 | 1;
}>;

/**
 * Helper — czy pole jest WIDOCZNE w form (value `'1'`/`1` lub `'2'`/`2`).
 * Mirror PHP `IUSERS_MANAGER_CONTROLLER::didShowField` (PHP używa loose `==`
 * porównania w switch case — my w JS robimy explicit string OR number match).
 *
 * Tolerancyjne porównanie: backend może serializować jako string `"1"` lub
 * number `1` zależnie od źródła wartości (form POST -> string, hardcoded int -> number).
 */
export function didShowField(
  config: Record<string, FieldVisibility> | null | undefined,
  field: string,
): boolean {
  if (!config) return false;
  const v = config[field];
  return v === "1" || v === "2" || v === 1 || v === 2;
}

/**
 * Helper — czy pole jest REQUIRED (value `'2'` lub `2`).
 * Mirror PHP `IUSERS_MANAGER_CONTROLLER::isFieldRequired`.
 */
export function isFieldRequired(
  config: Record<string, FieldVisibility> | null | undefined,
  field: string,
): boolean {
  if (!config) return false;
  const v = config[field];
  return v === "2" || v === 2;
}
